import { useState, useEffect, useCallback } from "react";
import supabase from "@/lib/supabase";

export interface ClassSchedule {
  id: number;
  cohort_id: number;
  name: string;
  days: number[];
  start_time: string;
  end_time: string;
  start_date: string;
  end_date: string;
  sort_order: number;
  created_at: string;
}

const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

export function daysToLabel(days: number[]): string {
  return days
    .sort((a, b) => a - b)
    .map((d) => DAY_NAMES[d])
    .join("");
}

export function formatScheduleLabel(schedule: ClassSchedule): string {
  const dayLabel = daysToLabel(schedule.days);
  const [m, d] = schedule.start_date.split("-").slice(1);
  const month = parseInt(m, 10);
  const day = parseInt(d, 10);
  return `${schedule.name} ${schedule.start_time} (${month}/${day} 개강)`;
}

export function formatDateKorean(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const dow = DAY_NAMES[date.getDay()];
  return `${m}/${d}(${dow})`;
}

export function formatDateRangeKorean(startStr: string, endStr: string): string {
  const startParts = startStr.split("-").map(Number);
  const endParts = endStr.split("-").map(Number);
  const start = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);
  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  const weeks = Math.ceil(diffDays / 7);
  return `${formatDateKorean(startStr)} ~ ${formatDateKorean(endStr)}, ${weeks}주`;
}

export default function useClassSchedules(cohortId: number | null) {
  const [schedules, setSchedules] = useState<ClassSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    if (!cohortId) {
      setSchedules([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from("class_schedules")
        .select("*")
        .eq("cohort_id", cohortId)
        .order("sort_order", { ascending: true });

      if (fetchError) throw fetchError;
      setSchedules((data as ClassSchedule[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "반 일정을 불러오지 못했습니다");
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  const createSchedule = useCallback(
    async (data: Omit<ClassSchedule, "id" | "created_at">) => {
      if (!cohortId) return null;
      const { data: created, error: insertError } = await supabase
        .from("class_schedules")
        .insert({ ...data, cohort_id: cohortId })
        .select()
        .single();

      if (insertError) throw insertError;

      const schedule = created as ClassSchedule;
      setSchedules((prev) => [...prev, schedule]);
      return schedule;
    },
    [cohortId]
  );

  const updateSchedule = useCallback(
    async (id: number, data: Partial<Omit<ClassSchedule, "id" | "cohort_id" | "created_at">>) => {
      const { data: updated, error: updateError } = await supabase
        .from("class_schedules")
        .update(data)
        .eq("id", id)
        .select()
        .single();

      if (updateError) throw updateError;

      const schedule = updated as ClassSchedule;
      setSchedules((prev) => prev.map((s) => (s.id === id ? schedule : s)));
      return schedule;
    },
    []
  );

  const deleteSchedule = useCallback(async (id: number) => {
    const { error: deleteError } = await supabase
      .from("class_schedules")
      .delete()
      .eq("id", id);

    if (deleteError) throw deleteError;
    setSchedules((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return {
    schedules,
    loading,
    error,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    refetch: fetchSchedules,
  };
}